/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, DestroyRef, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { AuthenticationService } from '@alfresco/adf-core';
import { TranslatePipe } from '@ngx-translate/core';
import { AppExtensionService, AppSettingsService, ToolbarComponent } from '@alfresco/aca-shared';
import { ProfileMenuComponent } from '../theme/profile-menu.component';
import { AppNotificationsComponent } from '../notifications/app-notifications.component';
import { BrandingService } from '../branding/branding.service';
import { WatermarkService } from '../watermark/watermark.service';
import { WorkflowSettingsService } from '../workflow/workflow-settings.service';
import { MenuPermissionsService } from '../menu-permissions/menu-permissions.service';
import { DisplayPreferencesService } from '../shared/display-preferences.service';
import { CommandPaletteService } from '../command-palette/command-palette.service';
import { OnboardingService } from '../onboarding/onboarding.service';
import { WhatsNewService } from '../version/whats-new.service';
import { ContentActionRef } from '@alfresco/adf-extensions';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * Global shell top bar.
 *
 * Provides a Thai-first, search-first header that sits inside the ADF
 * shell header extension point: the brand on the left, the search centred on
 * the window, and notifications, help and the user menu on the right. It
 * routes the user to the real /search page on submit and reuses the existing
 * header action extension point for notifications.
 */
@Component({
  selector: 'app-shell-header',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatToolbarModule,
    MatTooltipModule,
    MatMenuModule,
    RouterModule,
    TranslatePipe,
    ToolbarComponent,
    ProfileMenuComponent,
    AppNotificationsComponent
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './shell-header.component.html',
  styleUrl: './shell-header.component.scss'
})
export class ShellHeaderComponent implements OnInit {
  private static readonly RAIL_CLASS = 'aca-nav-rail';
  private static readonly RAIL_KEY = 'aca.nav.collapsed';

  private readonly router = inject(Router);
  private readonly appExtensions = inject(AppExtensionService);
  private readonly appSettings = inject(AppSettingsService);
  readonly branding = inject(BrandingService);
  private readonly watermarks = inject(WatermarkService);
  private readonly workflowSettings = inject(WorkflowSettingsService);
  private readonly menuPermissions = inject(MenuPermissionsService);
  private readonly displayPrefs = inject(DisplayPreferencesService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly auth = inject(AuthenticationService);
  private readonly onboarding = inject(OnboardingService);
  readonly palette = inject(CommandPaletteService);
  readonly whatsNew = inject(WhatsNewService);

  /** Injected by `adf-dynamic-component`; carries the shell `layout` instance. */
  data: { layout?: { toggleMenu: () => void } } = {};

  /** Whether the navigation is showing as an icon rail. */
  navCollapsed = false;

  searchTerm = '';
  actions: ContentActionRef[] = [];

  readonly landingPage = this.appSettings.landingPage;

  /** The help menu's last entry: Alfresco's own documentation, for what the
   *  tour and this application's pages do not cover. */
  readonly helpUrl = 'https://support.hyland.com/access?dita:id=alfresco-content-services';

  startTour(): void {
    this.onboarding.start(this.auth.getUsername() ?? '');
  }

  ngOnInit(): void {
    this.branding.load();
    this.watermarks.load();
    this.workflowSettings.load();
    this.menuPermissions.load();
    // the page size chosen in Settings, handed to ADF's lists for this session
    this.displayPrefs.apply();
    this.restoreNavCollapsed();
    this.trackRoute();
    this.appExtensions
      .getHeaderActions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((actions) => {
        this.actions = actions;
      });
  }

  /**
   * Collapses the navigation to an icon rail, or restores it.
   *
   * Not ADF's `layout.toggleMenu()`: on a desktop width that sets the layout's
   * own `hideSidenav`, which closes the drawer outright, so the navigation
   * disappears — labels, icons and all — instead of narrowing. On a phone the
   * drawer is an overlay and closing it IS the right behaviour, so ADF still
   * handles that case.
   */
  toggleMenu(): void {
    if (window.matchMedia('(max-width: 600px)').matches) {
      this.data?.layout?.toggleMenu();
      return;
    }
    this.setNavCollapsed(!this.navCollapsed);
  }

  /**
   * Publishes the active route on the body element so page-specific styling can
   * be scoped to one screen. ACA shares one file-list component across several
   * routes, so styling it by component alone would reach screens this
   * application does not intend to change.
   */
  private trackRoute(): void {
    const publish = (url: string) => document.body.setAttribute('data-aca-route', url.split('?')[0]);
    publish(this.router.url);
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => publish(event.urlAfterRedirects));
  }

  private setNavCollapsed(collapsed: boolean): void {
    this.navCollapsed = collapsed;
    document.body.classList.toggle(ShellHeaderComponent.RAIL_CLASS, collapsed);
    try {
      localStorage.setItem(ShellHeaderComponent.RAIL_KEY, String(collapsed));
    } catch {
      // a browser that refuses storage still gets the toggle, just not remembered
    }
  }

  private restoreNavCollapsed(): void {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(ShellHeaderComponent.RAIL_KEY);
    } catch {
      stored = null;
    }
    this.setNavCollapsed(stored === 'true');
  }

  onSearchSubmit(event: Event): void {
    event.preventDefault();
    const term = this.searchTerm?.trim();
    if (!term) {
      return;
    }
    void this.router.navigate(['/document-search'], { queryParams: { q: term } });
  }
}
